import { createFileRoute } from '@tanstack/react-router'
import { AlternativesPage, alternativesHead, type AlternativesData } from '@/components/alternatives-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

const data: AlternativesData = {
  tool: "Grafana Cloud",
  slug: "grafana",
  intro: "Grafana Cloud is a flexible observability stack, and its synthetic checks use k6 scripts. If you want a turnkey tool or flat pricing, these alternatives are options.",
  alternatives: [
    {
      name: "Datadog",
      description: "Full observability platform covering APM, logs, infrastructure, and synthetic monitoring, billed by usage.",
      pricing: "Synthetics: $5 per 10k API runs, $12 per 1k browser runs (annual billing)",
      pros: ["Full-stack observability in one platform", "API and browser synthetic tests", "Retries and multi-location alert rules"],
      cons: ["No free tier for Synthetics", "Usage-based billing varies month to month"],
      url: "https://www.datadoghq.com",
    },
    {
      name: "Uptrack",
      description: "If you only use Grafana Cloud for uptime checks, Uptrack runs them without scripts: 30-second checks with alert confirmation, from $3/mo.",
      pricing: "Free (5 monitors), Pro $3/mo, Team $20/mo, Business $79/mo",
      pros: ["30-second checks on every plan", "Alerts only after 2-5 consecutive failed checks", "Flat pricing with a 30-day money-back guarantee"],
      cons: ["Free plan is 5 monitors at 30 s from one location", MONITOR_LOCATIONS.alternativesCon, "No APM or logs"],
      url: "https://uptrack.app",
      isUptrack: true,
    },
    {
      name: "New Relic",
      description: "Full observability platform with a free tier of 100 GB ingest and 500 synthetic checks a month.",
      pricing: "Free (100 GB/mo), then per user plus $0.40/GB",
      pros: ["Free tier with 100 GB ingest", "APM, RUM, logs, and synthetics in one platform", "Multi-location alert conditions"],
      cons: ["Per-user pricing for full platform users", "Synthetic checks beyond 500 a month billed per check"],
      url: "https://newrelic.com",
    },
    {
      name: "Checkly",
      description: "Monitoring as code with Playwright browser checks, API checks, and CI/CD integration.",
      pricing: "Free (Hobby), Starter $24/mo",
      pros: ["Monitoring as code with Playwright", "CI/CD integration", "Configurable retry strategies"],
      cons: ["API and browser runs above plan limits are billed per run", "30-second checks start on Team ($64/mo)"],
      url: "https://www.checklyhq.com",
    },
    {
      name: "Better Stack",
      description: "Uptime monitoring combined with on-call, incident management, logs, and status pages in one platform.",
      pricing: "Free (10 monitors), paid from $34/mo per responder",
      pros: ["On-call scheduling and incident management", "Unlimited phone and SMS alerts on paid plans", "Multi-location confirmation (3 of 4 locations by default)"],
      cons: ["Priced per responder, plus monitor packs", "More product than you need if you only want uptime checks"],
      url: "https://betterstack.com",
    },
  ],
  comparisonTable: [
    { tool: "Datadog", freePlan: "None (synthetics)", checkInterval: "Per test", alertConfirmation: "Configurable", startingPrice: "$5 per 10k API runs" },
    { tool: "Uptrack", freePlan: "5 monitors at 30s", checkInterval: "30s (all plans)", alertConfirmation: "Yes (2-5 consecutive)", startingPrice: "Free; Pro $3/mo" },
    { tool: "New Relic", freePlan: "100 GB + 500 checks", checkInterval: "1min", alertConfirmation: "Configurable", startingPrice: "Per user + usage" },
    { tool: "Checkly", freePlan: "Hobby (10 uptime monitors)", checkInterval: "2min (free), 30s (Team)", alertConfirmation: "Configurable retries", startingPrice: "$24/mo" },
    { tool: "Better Stack", freePlan: "10 monitors", checkInterval: "3min (free), 30s (paid)", alertConfirmation: "Yes (multi-location)", startingPrice: "$34/mo per responder" },
  ],
}

export const Route = createFileRoute('/_site/alternatives/grafana')({
  component: () => <AlternativesPage data={data} />,
  head: () => alternativesHead(data),
})
